import type { CognitoIApi, SignUpInput } from '@/infra/cognitoIApi';
import type { WagakoAppIApi } from '@/infra/wagakoAppIApi';
import type { WagakoNurseryIApi } from '@/infra/wagakoNurseryIApi';

import { DomainServiceBase } from '@/domain/domainServiceBase';

/**
 * ユーザードメインサービス
 */
export class UserDomainService extends DomainServiceBase {
  private cognitoApi!: CognitoIApi;

  private wagakoAppApi!: WagakoAppIApi;

  private wagakoNurseryApi!: WagakoNurseryIApi;

  /**
   * Injection
   * @param cognitoApi cognitoApi
   * @param wagakoAppApi wagakoAppApi
   * @param wagakoNurseryApi wagakoNurseryApi
   */
  public inject(
    cognitoApi: CognitoIApi,
    wagakoAppApi: WagakoAppIApi,
    wagakoNurseryApi: WagakoNurseryIApi,
  ) {
    this.cognitoApi = cognitoApi;
    this.wagakoAppApi = wagakoAppApi;
    this.wagakoNurseryApi = wagakoNurseryApi;
  }

  /**
   * ユーザー情報取得
   * @returns ユーザー情報
   */
  async getUserInfo() {
    const user = await this.cognitoApi.getUserInfo();
    return user;
  }

  /**
   * ログイン
   * @param username ユーザーID
   * @param password パスワード
   * @returns 結果
   */
  async signIn(
    username: string,
    password: string,
  ): Promise<{
    /** ステータス */
    status: 'ok' | 'confirm' | 'error',
    /** メッセージ */
    msg: string,
  }> {
    const result = await this.cognitoApi.signIn(username, password);
    if (result.status === 'ok') {
      return {
        status: 'ok',
        msg: result.msg,
      };
    }
    // error
    if (result.flg?.isConfirmedError) {
      return {
        status: 'confirm',
        msg: result.msg,
      };
    }
    return {
      status: 'error',
      msg: result.msg,
    };
  }

  /**
   * ログアウト
   */
  async signOut() {
    await this.cognitoApi.signOut();
  }

  /**
   * 登録
   * @param input 入力
   * @returns 結果
   */
  async signUp(
    input: SignUpInput,
  ): Promise<{
    /** ステータス */
    status: 'ok' | 'error',
    /** メッセージ */
    msg: string,
  }> {
    const { status, msg } = await this.cognitoApi.signUp(input);
    if (status === 'ok') {
      return {
        status: 'ok',
        msg,
      };
    }
    return {
      status: 'error',
      msg,
    };
  }

  /**
   * 登録の再送信.
   * @param username ユーザー名
   * @returns 結果
   */
  async resendSignUp(
    username: string,
  ): Promise<{
    /** ステータス */
    status: 'ok' | 'error',
    /** メッセージ */
    msg: string,
  }> {
    const { status, msg } = await this.cognitoApi.resendSignUp(username);
    if (status === 'ok') {
      return { status: 'ok', msg };
    }
    return { status: 'error', msg };
  }

  /**
   * 登録を確認
   * @param username ユーザー名
   * @param code コード
   * @returns 結果
   */
  async confirmSignUp(
    username: string,
    code: string,
  ): Promise<{
    /** ステータス */
    status: 'ok' | 'error',
    /** メッセージ */
    msg: string,
  }> {
    const { status, msg } = await this.cognitoApi.confirmSignUp(username, code);
    if (status === 'ok') {
      return { status: 'ok', msg };
    }
    return { status: 'error', msg };
  }

  /**
   * パスワードを忘れた
   * @param username ユーザー名
   * @returns 結果
   */
  async forgotPassword(username: string): Promise<{
    /** ステータス */
    status: 'ok' | 'error',
    /** メッセージ */
    msg: string,
  }> {
    const { status, msg } = await this.cognitoApi.forgotPassword(username);
    if (status === 'ok') {
      return { status: 'ok', msg };
    }
    return { status: 'error', msg };
  }

  /**
   * forgotPasswordSubmit
   * @param username username
   * @param code 認証コード
   * @param password パスワード
   * @returns 結果
   */
  async forgotPasswordSubmit(
    username: string,
    code: string,
    password: string,
  ): Promise<{
    /** ステータス */
    status: 'ok' | 'error',
    /** メッセージ */
    msg: string;
  }> {
    const { status, msg } = await this.cognitoApi.forgotPasswordSubmit(
      username,
      code,
      password,
    );
    if (status === 'ok') {
      return { status: 'ok', msg };
    }
    return { status: 'error', msg };
  }

  /**
   * メールアドレスを変更
   * @param email メールアドレス
   * @returns 結果
   */
  async changeEmail(email: string): Promise<{
    /** ステータス */
    status: 'ok' | 'error',
    /** メッセージ */
    msg: string,
  }> {
    const { status, msg } = await this.cognitoApi.changeEmail(email);
    if (status === 'ok') {
      return { status: 'ok', msg };
    }
    return { status: 'error', msg };
  }

  /**
   * resendChangeEmail
   * @returns 結果
   */
  async resendChangeEmail(): Promise<{
    /** ステータス */
    status: 'ok' | 'error',
    /** メッセージ */
    msg: string,
  }> {
    const { status, msg } = await this.cognitoApi.resendChangeEmail();
    if (status === 'ok') {
      return { status: 'ok', msg };
    }
    return { status: 'error', msg };
  }

  /**
   * メール検証
   * @param code コード
   * @param email メール
   * @returns 結果
   */
  async verifyEmail(code: string, email: string): Promise<{
    /** ステータス */
    status: 'ok' | 'error',
    /** メッセージ */
    msg: string,
  }> {
    const { status, msg } = await this.cognitoApi.verifyEmail(code);
    if (status === 'ok') {
      const res = await this.wagakoAppApi.updateUser({ email });
      if (res.message !== 'OK') {
        this.logger.error(`Error in verifyEmail: ${res.message}`);
        throw new Error('メールの検証で予期せぬエラーが起きました');
      }
      return { status: 'ok', msg };
    }
    return { status: 'error', msg };
  }

  /**
   * reportUpdateChild
   * @param nurseryId nurseryId
   * @param childId childId
   * @param email email
   */
  async reportUpdateChild(nurseryId: string, childId: string, email: string) {
    const res = await this.wagakoNurseryApi.updateChild(nurseryId, { childId, email });
    if (res.message !== 'OK') {
      this.logger.error(`Error in reportUpdateChild: ${res.message}`);
      throw new Error('メールを施設に伝える際に予期せぬエラーが起きました');
    }
    const resFacility = await this.wagakoAppApi.updateFacility(
      { facilityId: nurseryId, childId, email },
    );
    if (resFacility.message !== 'OK') {
      this.logger.error(`Error in reportUpdateChild: ${resFacility.message}`);
      throw new Error('メール上方修正の際に予期せぬエラーが起きました');
    }
  }
}
